import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { trekAPI, attractionAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import './ModuleAdmin.css';

const emptyAttraction = {
  name: '',
  order: 0
};

const TrekAttractionForm = () => {
  const { trekId, attractionId } = useParams();
  const isEdit = attractionId && attractionId !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: trekRes } = useQuery(
    ['trek', trekId],
    () => trekAPI.get(trekId),
    { enabled: !!trekId }
  );
  const trek = trekRes?.data?.data;

  const { data: globalAttractionsRes } = useQuery(['attractions'], () => attractionAPI.list());
  const globalAttractions = globalAttractionsRes?.data?.data || [];

  const { data: attractionRes } = useQuery(
    ['trek-attractions', trekId],
    () => trekAPI.listAttractions(trekId),
    { enabled: isEdit && !!trekId }
  );
  const existingAttraction = isEdit
    ? (attractionRes?.data?.data || []).find((a) => String(a.id) === String(attractionId))
    : null;

  const [form, setForm] = useState(emptyAttraction);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (existingAttraction) {
      setForm({
        name: existingAttraction.name || '',
        order: existingAttraction.order != null ? existingAttraction.order : 0
      });
    }
  }, [existingAttraction]);

  const mutation = useMutation(
    (payload) =>
      isEdit
        ? trekAPI.updateAttraction(trekId, attractionId, payload)
        : trekAPI.createAttraction(trekId, payload),
    {
      onSuccess: (response) => {
        if (!isMountedRef.current) return;
        queryClient.invalidateQueries(['trek-attractions', trekId]);
        if (!isEdit) {
          const createdId = response?.data?.data?.id;
          if (createdId) navigate(`/treks/${trekId}/attractions/${createdId}`, { replace: true });
        }
      }
    }
  );

  const buildPayload = () => ({
    name: form.name.trim(),
    order: form.order ? Number(form.order) : 0
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast({
        message: 'Attraction name is required.',
        severity: 'warning'
      });
      return;
    }
    const payload = buildPayload();
    mutation.mutate(payload);
  };

  return (
    <div className="module-page">
      <AdminBackLink to="/treks" state={{ openTrekId: trekId }} label="Back to treks" />
      <div className="module-header">
        <div>
          <h1 className="module-title">{isEdit ? 'Edit Attraction' : 'Add Attraction'}</h1>
          <p className="module-subtitle">
            {trek ? trek.name : 'Add attraction details for trek'}
          </p>
        </div>
      </div>

      <div className="module-card">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Attraction</label>
              <select
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              >
                <option value="">Select an attraction</option>
                {globalAttractions.map((a) => (
                  <option key={a.id} value={a.name}>
                    {a.name}
                  </option>
                ))}
                {form.name && !globalAttractions.some((a) => a.name === form.name) && (
                  <option value={form.name}>{form.name}</option>
                )}
              </select>
            </div>
            <div>
              <label>Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
              />
            </div>
          </div>

          <div className="legacy-main-actions">
            <button className="module-btn" type="button" disabled={mutation.isLoading} onClick={handleSubmit}>
              {isEdit ? 'Update Attraction' : 'Save Attraction'}
            </button>
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => navigate('/treks', { state: { openTrekId: trekId } })}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrekAttractionForm;

